import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { AppContext } from "@/context/AppContext";
import { gameApi, siteApi } from "@/lib/api";
import CashierModal from "@/components/Modals/CashierModals/CashierModal";
import {
  firstValue,
  formatMoney,
  getProfileAccountNumber,
  getProfileBalance,
  getProfileDepositorName,
  getProfileUsdtAddress,
  getResponseRows,
  getSelectedBank,
  maskAccountNumber,
} from "@/components/Modals/CashierModals/financeWallet.helpers";
import { StyleProfile } from "./styles";

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  focusable: "false",
  "aria-hidden": "true",
};

const GearIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3a2 2 0 1 1 4 0v.1A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.6.2 1 .8 1.1 1.4H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5.6Z" />
  </svg>
);

const cleanValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return String(value);
};

const getProfileName = (profile = {}) =>
  cleanValue(
    firstValue(profile, [
      "nick_name",
      "nickname",
      "name",
      "displayName",
      "username",
      "user_name",
      "user_unique_id",
      "member_id",
    ])
  );

const getProfileId = (profile = {}) =>
  cleanValue(firstValue(profile, ["user_unique_id", "member_id", "user_id", "username", "id"]));

const getProfileLevel = (profile = {}) => {
  const level = firstValue(profile, ["level", "user_info.level", "member_level"]);
  if (level === undefined) return "-";
  return `LV. ${level}`;
};

const getBankName = (profile = {}) =>
  cleanValue(
    firstValue(profile, [
      "bank.bank_name",
      "bank.bankName",
      "bank.name",
      "bank.label",
      "bank.title",
      "bank.display_name",
      "user_info.bank.bank_name",
      "user_info.bank.bankName",
      "user_info.bank.name",
      "user_info.bank.label",
      "user_info.bank.title",
      "user_info.bank.display_name",
      "user_info.bank_name",
      "user_info.bankName",
      "user_info.bank_label",
      "user_info.bank_title",
      "bank_name",
      "bankName",
      "bank_label",
      "bank_title",
    ])
  );

const getBankId = (profile = {}) =>
  firstValue(profile, ["user_info.bank_id", "bank_id", "bank.id", "user_info.bank.id"]);

const getBankRowName = (bank = {}) =>
  cleanValue(
    firstValue(bank, [
      "bank_name",
      "bankName",
      "name",
      "label",
      "title",
      "display_name",
    ])
  );

const getReferralCode = (profile = {}) =>
  cleanValue(
    firstValue(profile, [
      "referral_code",
      "referralCode",
      "ref_code",
      "refCode",
      "recommend_code",
      "recommendCode",
      "recommended_code",
      "recommendedCode",
      "recommender_code",
      "recommenderCode",
      "invite_code",
      "inviteCode",
      "join_code",
      "joinCode",
      "registration_code",
      "registrationCode",
      "affiliate_referral_code",
      "affiliateReferralCode",
      "user_info.referral_code",
      "user_info.referralCode",
      "user_info.ref_code",
      "user_info.refCode",
      "user_info.recommend_code",
      "user_info.recommendCode",
      "user_info.recommended_code",
      "user_info.recommendedCode",
      "user_info.recommender_code",
      "user_info.recommenderCode",
      "user_info.invite_code",
      "user_info.inviteCode",
      "user_info.join_code",
      "user_info.joinCode",
      "user_info.registration_code",
      "user_info.registrationCode",
      "user_info.affiliate_referral_code",
      "user_info.affiliateReferralCode",
      "recommender.user_unique_id",
      "recommender.user_id",
      "referrer.user_unique_id",
      "referrer.user_id",
    ])
  );

const isSuccessResult = (result) =>
  Boolean(result && result.success !== false && result.status !== false && !result.error);

const getResultMessage = (result, fallback) => result?.message || result?.error || fallback;

const NoticeModal = ({ notice, onClose }) => {
  if (!notice) return null;

  return (
    <div className="mypage-notice-backdrop" role="alertdialog" aria-modal="true">
      <div className={`mypage-notice-panel ${notice.type || "info"}`}>
        <strong>{notice.type === "success" ? "처리 완료" : "확인 필요"}</strong>
        <p>{notice.message}</p>
        <button type="button" onClick={onClose}>
          확인
        </button>
      </div>
    </div>
  );
};

const ReadOnlyField = ({ label, value }) => (
  <div className="mypage-field">
    <label>{label}</label>
    <div className="mypage-readonly-value">{value}</div>
  </div>
);

const BalanceRow = ({ label, value, unit }) => (
  <div className="mypage-balance-row">
    <span>{label}</span>
    <strong>
      {value}
      {unit ? <em>{unit}</em> : null}
    </strong>
  </div>
);

const ProfileSection = ({ title, description, action, children }) => (
  <section className="mypage-panel">
    <div className="mypage-section-head">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Profile = () => {
  const { updateAttendanceModal } = useContext(AppContext);
  const { user, refreshProfile, signOut } = useAuth();
  const profile = useMemo(() => user?.profile || {}, [user?.profile]);
  const [bankListRows, setBankListRows] = useState([]);
  const [isBankLoading, setIsBankLoading] = useState(false);
  const [passwordValues, setPasswordValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [notice, setNotice] = useState(null);

  const accountRows = useMemo(
    () => [
      { label: "아이디", value: getProfileId(profile) },
      { label: "닉네임", value: getProfileName(profile) },
      { label: "레벨", value: getProfileLevel(profile) },
      {
        label: "추천인 코드",
        value: getReferralCode(profile),
      },
    ],
    [profile]
  );

  const profileBankName = useMemo(() => getBankName(profile), [profile]);
  const profileBankId = useMemo(() => getBankId(profile), [profile]);
  const selectedBank = useMemo(() => getSelectedBank(bankListRows, profile), [bankListRows, profile]);
  const bankName = useMemo(() => {
    if (profileBankName !== "-") return profileBankName;
    if (isBankLoading) return "확인 중";
    return getBankRowName(selectedBank);
  }, [isBankLoading, profileBankName, selectedBank]);

  const bankRows = useMemo(
    () => [
      { label: "예금주", value: cleanValue(getProfileDepositorName(profile)) },
      { label: "은행", value: bankName },
      { label: "계좌번호", value: maskAccountNumber(getProfileAccountNumber(profile)) },
      { label: "USDT 지갑", value: cleanValue(getProfileUsdtAddress(profile)) },
    ],
    [bankName, profile]
  );

  const balances = useMemo(
    () => [
      {
        label: "보유금",
        value: formatMoney(getProfileBalance(profile, "money")),
        unit: "원",
      },
      {
        label: "포인트",
        value: formatMoney(getProfileBalance(profile, "point")),
        unit: "P",
      },
      {
        label: "콤프",
        value: formatMoney(getProfileBalance(profile, "comp")),
        unit: "P",
      },
      {
        label: "홀덤머니",
        value: formatMoney(getProfileBalance(profile, "holdemMoney")),
        unit: "원",
      },
      {
        label: "홀덤콤프",
        value: formatMoney(getProfileBalance(profile, "holdemComp")),
        unit: "P",
      },
    ],
    [profile]
  );

  const quickLinks = [
    { label: "고객문의", to: "/customer-service-center" },
    { label: "출석체크", modal: "attendance" },
    { label: "쪽지", to: "/notice-board" },
    { label: "룰렛", to: "/roulette" },
  ];

  useEffect(() => {
    if (profileBankName !== "-" || !profileBankId) {
      setBankListRows([]);
      setIsBankLoading(false);
      return undefined;
    }

    let mounted = true;
    setIsBankLoading(true);
    siteApi
      .getBanks({ limit: "ALL" })
      .then((result) => {
        if (!mounted) return;
        setBankListRows(isSuccessResult(result) ? getResponseRows(result.data) : []);
      })
      .finally(() => {
        if (mounted) setIsBankLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [profileBankId, profileBankName]);

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordValues((previous) => ({ ...previous, [name]: value }));
  };

  const openNotice = (type, message) => setNotice({ type, message });

  const submitPassword = async (event) => {
    event.preventDefault();
    const currentPassword = passwordValues.currentPassword.trim();
    const newPassword = passwordValues.newPassword.trim();
    const confirmPassword = passwordValues.confirmPassword.trim();

    if (!currentPassword) {
      openNotice("error", "현재 비밀번호를 입력해주세요.");
      return;
    }
    if (!newPassword) {
      openNotice("error", "새 비밀번호를 입력해주세요.");
      return;
    }
    if (newPassword.length < 6) {
      openNotice("error", "새 비밀번호는 6자 이상 입력해주세요.");
      return;
    }
    if (newPassword !== confirmPassword) {
      openNotice("error", "새 비밀번호 확인이 일치하지 않습니다.");
      return;
    }

    setIsSubmittingPassword(true);
    try {
      const result = await gameApi.user.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (!isSuccessResult(result)) {
        openNotice("error", getResultMessage(result, "비밀번호 변경에 실패했습니다."));
        return;
      }

      setPasswordValues({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      openNotice("success", getResultMessage(result, "비밀번호가 변경되었습니다."));
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const handleRefreshProfile = async () => {
    try {
      await refreshProfile();
      openNotice("success", "회원 정보를 새로 불러왔습니다.");
    } catch (error) {
      openNotice("error", "회원 정보를 새로 불러오지 못했습니다.");
    }
  };

  return (
    <StyleProfile>
      <NoticeModal notice={notice} onClose={() => setNotice(null)} />

      <header className="mypage-page-head">
        <GearIcon />
        <h1>마이페이지</h1>
      </header>

      <div className="mypage-main-grid">
        <ProfileSection
          title="계정"
          description="가입 정보를 확인합니다."
          action={
            <div className="section-actions">
              <span className="status-badge">확인됨</span>
              <button type="button" className="ghost-button" onClick={handleRefreshProfile}>
                새로고침
              </button>
            </div>
          }
        >
          <div className="mypage-field-grid">
            {accountRows.map((row) => (
              <ReadOnlyField key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        </ProfileSection>

        <ProfileSection
          title="입출금 정보"
          description="계좌와 USDT 주소는 등록된 정보만 표시됩니다."
        >
          <div className="mypage-field-grid">
            {bankRows.map((row) => (
              <ReadOnlyField key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        </ProfileSection>

        <ProfileSection title="보유 자산" description="현재 사용 가능한 잔액입니다.">
          <div className="mypage-balance-list" aria-label="보유 자산">
            {balances.map((item) => (
              <BalanceRow key={item.label} {...item} />
            ))}
          </div>
        </ProfileSection>

        <ProfileSection title="보안" description="현재 비밀번호 확인 후 새 비밀번호로 변경합니다.">
          <form className="mypage-form" onSubmit={submitPassword}>
            <label>
              <span>현재 비밀번호</span>
              <input
                type="password"
                name="currentPassword"
                value={passwordValues.currentPassword}
                onChange={handlePasswordChange}
                autoComplete="current-password"
                placeholder="현재 비밀번호"
              />
            </label>
            <label>
              <span>새 비밀번호</span>
              <input
                type="password"
                name="newPassword"
                value={passwordValues.newPassword}
                onChange={handlePasswordChange}
                autoComplete="new-password"
                placeholder="6자 이상"
              />
            </label>
            <label>
              <span>새 비밀번호 확인</span>
              <input
                type="password"
                name="confirmPassword"
                value={passwordValues.confirmPassword}
                onChange={handlePasswordChange}
                autoComplete="new-password"
                placeholder="새 비밀번호 재입력"
              />
            </label>
            <button type="submit" className="primary-button" disabled={isSubmittingPassword}>
              {isSubmittingPassword ? "변경 중" : "비밀번호 변경"}
            </button>
          </form>
        </ProfileSection>

        <ProfileSection title="바로가기" description="입출금과 혜택 전환은 지갑에서 처리합니다.">
          <div className="mypage-wallet-cta">
            <CashierModal button="지갑 열기" triggerClassName="mypage-wallet-button" />
          </div>
          <div className="mypage-link-grid">
            {quickLinks.map((item) => (
              item.modal === "attendance" ? (
                <button
                  key={item.label}
                  type="button"
                  className="mypage-link-card"
                  onClick={() => updateAttendanceModal(true)}
                >
                  {item.label}
                </button>
              ) : (
                <Link key={item.label} to={item.to} className="mypage-link-card">
                  {item.label}
                </Link>
              )
            ))}
          </div>
          <button type="button" className="logout-button" onClick={signOut}>
            로그아웃
          </button>
        </ProfileSection>
      </div>
    </StyleProfile>
  );
};

export default Profile;
